/**
 * OrgParticipantDetail - person profile matched to reference participant page.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LpWorkbenchShell from '../components/org/LpWorkbenchShell';
import { orgAuthApi } from '../services/api';
import { getLoginPath } from '../utils/postLoginNavigation';
import { buildParticipantProfileView } from '../utils/lpParticipantProfile';
import '../styles/LpWorkbench.css';
import '../styles/LpParticipantDetail.css';

function StatusRow({ label, status, tone }) {
  return (
    <div className="lp-pp-ref-row">
      <span>{label}</span>
      <span className={`lp-pp-stat ${tone}`}>{status}</span>
    </div>
  );
}

export default function OrgParticipantDetail() {
  const { productionId, participantId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const fromImmigration = searchParams.get('from') === 'immigration';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await orgAuthApi.getProductionParticipant(productionId, participantId);
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setError('Could not load this participant.');
    } finally {
      setLoading(false);
    }
  }, [productionId, participantId, navigate]);

  useEffect(() => { load(); }, [load]);

  const view = useMemo(() => {
    if (!data?.participant) return null;
    return buildParticipantProfileView({
      project: data.project,
      participant: data.participant,
      caseInfo: data.case,
      timeline: data.timeline,
      checklist: data.document_checklist,
      todos: data.todos,
      openItemsCount: data.open_items_count,
      nda: data.nda,
      safety: data.safety,
      registration: data.registration,
      itinerary: data.itinerary,
      sections: data.sections,
      fromImmigration,
    });
  }, [data, fromImmigration]);

  const backTo = fromImmigration
    ? `/org/productions/${productionId}?tab=immigration`
    : `/org/productions/${productionId}?tab=overview`;
  const backLabel = fromImmigration ? 'Participants' : 'Overview';

  return (
    <LpWorkbenchShell slateTheme hideLanguage compactProfile>
      <div className="lp-body">
        <div className="lp-pp-page lp-pp-ref">
          <Link to={backTo} className="lp-pp-back">
            <span aria-hidden="true">&lt;</span>
            {backLabel}
          </Link>

          {loading ? (
            <div className="lp-empty">Loading…</div>
          ) : error ? (
            <div className="lp-banner" style={{ background: '#FBF1EF', borderColor: '#E8C4BE' }}>
              <div className="lp-bn-txt"><b>{error}</b></div>
            </div>
          ) : view ? (
            <>
              <h1 className="lp-pp-name">{view.name}</h1>

              <section className="lp-pp-ref-block">
                <h2 className="lp-pp-ref-h">Profile</h2>
                <div className="lp-pp-ref-row">
                  <span>Role</span>
                  <b>{view.role}</b>
                </div>
                <div className="lp-pp-ref-row">
                  <span>Coming from</span>
                  <b>{view.nationality}</b>
                </div>
                <div className="lp-pp-ref-row">
                  <span>Needed in</span>
                  <b>{data?.participant?.location || data?.project?.destination_country || '—'}</b>
                </div>
                <div className="lp-pp-ref-row">
                  <span>Needed by</span>
                  <b>{view.neededBy || '—'}</b>
                </div>
              </section>

              <section className="lp-pp-ref-block">
                <h2 className="lp-pp-ref-h">Professional profile</h2>
                {(view.sectionRows?.professional || []).map((row) => (
                  <StatusRow key={row.key} label={row.label} status={row.status} tone={row.tone} />
                ))}
              </section>

              <section className="lp-pp-ref-block">
                <h2 className="lp-pp-ref-h">Your details</h2>
                {(view.sectionRows?.details || []).map((row) => (
                  <StatusRow key={row.key} label={row.label} status={row.status} tone={row.tone} />
                ))}
              </section>

              <section className="lp-pp-ref-block">
                <h2 className="lp-pp-ref-h">Travel documents</h2>
                {(view.sectionRows?.travel || []).map((row) => (
                  <StatusRow key={row.key} label={row.label} status={row.status} tone={row.tone} />
                ))}
              </section>

              <section className="lp-pp-ref-block">
                <h2 className="lp-pp-ref-h">Immigration</h2>
                <div className="lp-pp-ref-meta">
                  <div>
                    <span>Route</span>
                    <b>{view.immigration.route}</b>
                  </div>
                  <div>
                    <span>Stage</span>
                    <b>{view.immigration.stage}</b>
                  </div>
                  <div>
                    <span>Advisor</span>
                    <b>{view.immigration.advisor}</b>
                  </div>
                </div>
                {(view.sectionRows?.immigration || []).map((row) => (
                  <StatusRow key={row.key} label={row.label} status={row.status} tone={row.tone} />
                ))}
              </section>
            </>
          ) : null}
        </div>
      </div>
    </LpWorkbenchShell>
  );
}
